import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS, QUARTER_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

export interface IGamesFilterValues {
  quarter: string;
  level: string;
  status: string;
}

interface IGamesFilterModalProps {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IGamesFilterValues) => void;
  initialValues?: Partial<IGamesFilterValues>;
  levelOptions?: { value: string; label: string }[];
}

const GamesFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
  levelOptions = [],
}: IGamesFilterModalProps) => {
  const { formatText } = useFormatText();

  const [quarter, setQuarter] = useState<string>(initialValues.quarter || '');
  const [level, setLevel] = useState<string>(initialValues.level || '');
  const [status, setStatus] = useState<string>(initialValues.status || '');

  const handleClear = () => {
    setQuarter('');
    setLevel('');
    setStatus('');
    onApply({ quarter: '', level: '', status: '' });
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply({
      quarter,
      level,
      status,
    });
    setIsOpen(false);
  };

  const isDisableApply = !quarter && !level && !status;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: isDisableApply,
        className: 'bg-purple-600 hover:bg-purple-700',
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        variant: 'link',
        onClick: handleClear,
        className: 'text-sm px-0! text-neutral-primary!',
      }}
    >
      <div className="flex flex-col gap-5 w-full py-4">
        <Select
          name={formatText('report-analytics.games.filters.quarter')}
          options={QUARTER_OPTIONS}
          selected={quarter}
          onChange={(value) => setQuarter(value as string)}
          placeholder={formatText(
            'report-analytics.games.filters.quarter-placeholder'
          )}
          isMulti={false}
          isClearable={true}
          className="min-h-13"
        />

        <Select
          name={formatText('report-analytics.games.filters.level')}
          options={levelOptions}
          selected={level}
          onChange={(value) => setLevel(value as string)}
          placeholder={formatText('common.select')}
          isMulti={false}
          isClearable={true}
          className="min-h-13"
        />

        <RadioButtonGroup
          name="status"
          label={formatText('report-analytics.games.table.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={(label) => formatText(label)}
          gap="gap-8"
        />
      </div>
    </CustomModal>
  );
};

export default GamesFilterModal;
